import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { SponsoredAdSlot } from "../ecommersProduct/types";
import type { RootState } from "../../store";
import {
  STOREFRONT_ADS_ACTION_TYPES,
  type AdPlacement,
  type FetchAdSlotsBatchParams,
  type FetchAdSlotsParams,
  type StorefrontAdsState,
} from "./types";

const API_BASE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ECOMMERCE_PERFIX,
]
  .filter(Boolean)
  .join("");

// Ad_Service's own NestJS app mounts its global prefix as REACT_APP_ADVERTISEMENTS_API_PERFIX
// (e.g. /api/v1/advertisement) — every route already lives under that path, so callers
// must not also append "/api/v1" themselves.
const AD_SERVICE_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_ADVERTISEMENTS_API_PERFIX,
]
  .filter(Boolean)
  .join("");

export const AD_SESSION_KEY = "salar_ad_session_id";

export const getAdSessionId = (): string => {
  let id = localStorage.getItem(AD_SESSION_KEY);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(AD_SESSION_KEY, id);
  }
  return id;
};

const initialState: StorefrontAdsState = {
  slotsByPlacement: {},
  loading: {},
  error: null,
};

export const fetchAdSlots = createAsyncThunk(
  STOREFRONT_ADS_ACTION_TYPES.FETCH_SLOTS,
  async (params: FetchAdSlotsParams, { rejectWithValue }) => {
    try {
      const sessionId = params.sessionId ?? getAdSessionId();
      const { data } = await axios.post(
        `${API_BASE_URL}/e-commerce/ads/slots`,
        {
          placement: params.placement,
          city: params.city || "all",
          sessionId,
          searchQuery: params.searchQuery,
          categoryType: params.categoryType,
          productId: params.productId,
          adTypes: params.adTypes,
          limit: params.limit,
        },
      );
      return {
        placement: params.placement as AdPlacement,
        slots: (data?.data?.slots ?? []) as SponsoredAdSlot[],
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to load ads";
      return rejectWithValue(message);
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState() as RootState;
      return !state.storefrontAds.loading[params.placement];
    },
  },
);

/** Direct Ad Service call (no product hydration) — fallback if BFF unavailable */
export const fetchAdSlotsDirect = createAsyncThunk(
  "storefrontAds/fetchSlotsDirect",
  async (params: FetchAdSlotsParams, { rejectWithValue }) => {
    try {
      const sessionId = params.sessionId ?? getAdSessionId();
      const { data } = await axios.post(
        `${AD_SERVICE_URL}/ads/storefront/serve`,
        {
          placement: params.placement,
          city: params.city || "all",
          pincode: params.pincode,
          sessionId,
          userId: params.userId,
          searchQuery: params.searchQuery,
          categoryType: params.categoryType,
          productId: params.productId,
          adTypes: params.adTypes,
          limit: params.limit,
        },
      );
      return {
        placement: params.placement as AdPlacement,
        slots: (data?.ads ?? []) as SponsoredAdSlot[],
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to load ads";
      return rejectWithValue(message);
    }
  },
);

export const fetchAdSlotsBatch = createAsyncThunk(
  STOREFRONT_ADS_ACTION_TYPES.FETCH_SLOTS_BATCH,
  async (params: FetchAdSlotsBatchParams, { rejectWithValue }) => {
    try {
      const sessionId = params.sessionId ?? getAdSessionId();
      const { data } = await axios.post(
        `${API_BASE_URL}/e-commerce/ads/slots/batch`,
        {
          city: params.city || "all",
          sessionId,
          placements: params.placements,
        },
      );
      return (data?.data?.placements ?? []) as Array<{
        placement: AdPlacement;
        slots: SponsoredAdSlot[];
      }>;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to load ads";
      return rejectWithValue(message);
    }
  },
);

const storefrontAdsSlice = createSlice({
  name: "storefrontAds",
  initialState,
  reducers: {
    clearPlacementSlots: (state, action: { payload: AdPlacement }) => {
      delete state.slotsByPlacement[action.payload];
    },
    clearAllAdSlots: (state) => {
      state.slotsByPlacement = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: StorefrontAdsState, placement: AdPlacement) => {
      state.loading[placement] = true;
      state.error = null;
    };
    const setSlots = (
      state: StorefrontAdsState,
      placement: AdPlacement,
      slots: SponsoredAdSlot[],
    ) => {
      state.slotsByPlacement[placement] = slots;
      state.loading[placement] = false;
    };

    builder
      .addCase(fetchAdSlots.pending, (state, action) => {
        setLoading(state, action.meta.arg.placement);
      })
      .addCase(fetchAdSlots.fulfilled, (state, action) => {
        setSlots(state, action.payload.placement, action.payload.slots);
      })
      .addCase(fetchAdSlots.rejected, (state, action) => {
        state.loading[action.meta.arg.placement] = false;
        state.error = (action.payload as string) ?? "Failed to load ads";
      })
      .addCase(fetchAdSlotsDirect.pending, (state, action) => {
        setLoading(state, action.meta.arg.placement);
      })
      .addCase(fetchAdSlotsDirect.fulfilled, (state, action) => {
        setSlots(state, action.payload.placement, action.payload.slots);
      })
      .addCase(fetchAdSlotsDirect.rejected, (state, action) => {
        state.loading[action.meta.arg.placement] = false;
        state.error = (action.payload as string) ?? "Failed to load ads";
      })
      .addCase(fetchAdSlotsBatch.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAdSlotsBatch.fulfilled, (state, action) => {
        for (const item of action.payload) {
          state.slotsByPlacement[item.placement] = item.slots;
          state.loading[item.placement] = false;
        }
      })
      .addCase(fetchAdSlotsBatch.rejected, (state, action) => {
        state.error = (action.payload as string) ?? "Failed to load ads";
      });
  },
});

export const { clearPlacementSlots, clearAllAdSlots } =
  storefrontAdsSlice.actions;
export default storefrontAdsSlice.reducer;
